/**
 * Drawn Signature Creator Component
 *
 * Allows consultants to draw their signature using a canvas.
 * Supports mouse and touch events for drawing.
 */

import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Paper,
    Grid,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    // IconButton,
} from '@mui/material';
import Button from 'views/Dashboards-B2B/components/Button';
import { Delete } from '@mui/icons-material';
import { AppDispatch } from 'redux/store';
import { createSignature } from 'redux/signatures/signatures.middleware';
import { selectSignaturesCreating } from 'redux/signatures/signatures.slice';
import {
    SIGNATURE_COLORS,
    SIGNATURE_SIZES,
    SIGNATURE_DEFAULTS,
    SIGNATURE_CONSTRAINTS,
} from 'config/signatureFonts';
import type { SignatureCreateRequest } from 'types/signature';

interface DrawnSignatureCreatorProps {
    onSuccess?: () => void;
}

export default function DrawnSignatureCreator({ onSuccess }: DrawnSignatureCreatorProps) {
    const dispatch = useDispatch<AppDispatch>();
    const isCreating = useSelector(selectSignaturesCreating);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    const [formData, setFormData] = useState({
        color: SIGNATURE_DEFAULTS.color,
        size: SIGNATURE_DEFAULTS.size as 'small' | 'medium' | 'large',
        display_name: '',
        title: '',
        signature_name: '',
        is_default: false,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set canvas size
        const container = canvas.parentElement;
        if (container) {
            canvas.width = container.clientWidth;
            canvas.height = 300;
        }

        // Set canvas background
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
    }, []);

    const getCoordinates = (event: MouseEvent | TouchEvent): { x: number; y: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const rect = canvas.getBoundingClientRect();
        let clientX: number, clientY: number;

        if (event instanceof MouseEvent) {
            clientX = event.clientX;
            clientY = event.clientY;
        } else {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top,
        };
    };

    const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
        event.preventDefault();
        setIsDrawing(true);
        setHasDrawn(true);

        const coords = getCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
        if (!coords) return;

        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
        }
    };

    const draw = (event: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing) return;
        event.preventDefault();

        const coords = getCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
        if (!coords) return;

        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
            ctx.strokeStyle = formData.color;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
        }
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            setHasDrawn(false);
        }
    };

    const canvasToSVG = (): string => {
        const canvas = canvasRef.current;
        if (!canvas) return '';

        // Convert canvas to data URL
        const dataURL = canvas.toDataURL('image/png');

        // Create SVG with embedded image
        const width = canvas.width;
        const height = canvas.height;

        return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <image href="${dataURL}" width="${width}" height="${height}" />
    </svg>`;
    };

    const handleChange = (field: string, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!hasDrawn) {
            newErrors.canvas = 'Please draw your signature';
        }

        if (
            formData.display_name &&
            formData.display_name.length > SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH
        ) {
            newErrors.display_name = `Display name must be less than ${SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH} characters`;
        }

        if (formData.title && formData.title.length > SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH) {
            newErrors.title = `Title must be less than ${SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH} characters`;
        }

        if (
            formData.signature_name &&
            formData.signature_name.length > SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH
        ) {
            newErrors.signature_name = `Signature name must be less than ${SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH} characters`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        const svgData = canvasToSVG();

        const payload: SignatureCreateRequest = {
            creation_method: 'drawn',
            drawn_svg_data: svgData,
            color: formData.color,
            size: formData.size,
            display_name: formData.display_name.trim() || undefined,
            title: formData.title.trim() || undefined,
            signature_name: formData.signature_name.trim() || undefined,
            is_default: formData.is_default,
        };

        try {
            await dispatch(createSignature(payload)).unwrap();
            // Reset form
            clearCanvas();
            setFormData({
                color: SIGNATURE_DEFAULTS.color,
                size: SIGNATURE_DEFAULTS.size as 'small' | 'medium' | 'large',
                display_name: '',
                title: '',
                signature_name: '',
                is_default: false,
            });
            onSuccess?.();
        } catch (error) {
            console.error('Failed to create signature:', error);
        }
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Canvas */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Draw Your Signature
                        </Typography>

                        <Paper
                            elevation={0}
                            sx={{
                                border: '2px solid #e0e0e0',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <canvas
                                ref={canvasRef}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                                style={{
                                    cursor: 'crosshair',
                                    width: '100%',
                                    display: 'block',
                                    touchAction: 'none',
                                }}
                            />
                        </Paper>

                        {errors.canvas && (
                            <Typography variant="caption" color="error">
                                {errors.canvas}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button border="purple" onClick={clearCanvas} style={{ width: '100%' }}>
                                <Delete style={{ marginRight: '8px' }} />
                                Clear
                            </Button>
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Pen Color</InputLabel>
                            <Select
                                value={formData.color}
                                label="Pen Color"
                                onChange={(e) => handleChange('color', e.target.value)}
                            >
                                {SIGNATURE_COLORS.map((color) => (
                                    <MenuItem key={color.value} value={color.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: 1,
                                                    backgroundColor: color.value,
                                                    border: '1px solid #ccc',
                                                }}
                                            />
                                            {color.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Display Size</InputLabel>
                            <Select
                                value={formData.size}
                                label="Display Size"
                                onChange={(e) => handleChange('size', e.target.value)}
                            >
                                {SIGNATURE_SIZES.map((size) => (
                                    <MenuItem key={size.value} value={size.value}>
                                        {size.name} {size.description && `- ${size.description}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>

                {/* Metadata */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Signature Details (Optional)
                        </Typography>

                        <TextField
                            label="Display Name"
                            placeholder="e.g., Dr. John Smith"
                            value={formData.display_name}
                            onChange={(e) => handleChange('display_name', e.target.value)}
                            error={!!errors.display_name}
                            helperText={errors.display_name || 'Override your name in agreements'}
                            fullWidth
                        />

                        <TextField
                            label="Professional Title"
                            placeholder="e.g., RCIC, Immigration Consultant"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            error={!!errors.title}
                            helperText={errors.title || 'Credentials displayed below signature'}
                            fullWidth
                        />

                        <TextField
                            label="Signature Name"
                            placeholder="e.g., Formal Signature"
                            value={formData.signature_name}
                            onChange={(e) => handleChange('signature_name', e.target.value)}
                            error={!!errors.signature_name}
                            helperText={errors.signature_name || 'Label to identify this signature'}
                            fullWidth
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.is_default}
                                    onChange={(e) => handleChange('is_default', e.target.checked)}
                                />
                            }
                            label="Set as default signature"
                        />

                        <Button
                            background="purple"
                            onClick={handleSubmit}
                            disabled={isCreating || !hasDrawn}
                        >
                            {isCreating && (
                                <CircularProgress size={20} style={{ marginRight: '8px' }} />
                            )}
                            {isCreating ? 'Creating...' : 'Create Signature'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
